import { currentLocale } from './i18n.js';

let timezone = 'America/Sao_Paulo';
export const setDisplayTimezone = (tz: string) => {
  timezone = tz;
};
export const displayTimezone = () => timezone;

/** Absolute date-time in the user's locale and profile time zone. */
export function formatDateTime(iso: string | null | undefined, options: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' }): string {
  if (!iso) return '';
  return new Intl.DateTimeFormat(currentLocale(), { ...options, timeZone: timezone }).format(new Date(iso));
}

export function formatDate(iso: string | null | undefined): string {
  return formatDateTime(iso, { dateStyle: 'medium' });
}

/** A calendar day (YYYY-MM-DD) in the user's locale, without any time-zone shift. */
export function formatCalendarDate(date: string | null | undefined, options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' }): string {
  if (!date) return '';
  return new Intl.DateTimeFormat(currentLocale(), { ...options, timeZone: 'UTC' }).format(new Date(`${date}T00:00:00Z`));
}

/** Today as YYYY-MM-DD in the profile time zone (due dates flip at the user's midnight). */
export function todayInDisplayZone(now = new Date()): string {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
  } catch {
    return now.toISOString().slice(0, 10);
  }
}

const UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 365 * 24 * 3600],
  ['month', 30 * 24 * 3600],
  ['week', 7 * 24 * 3600],
  ['day', 24 * 3600],
  ['hour', 3600],
  ['minute', 60],
];

/** Relative time ("3 hours ago") localized; seconds round to "now". */
export function formatRelative(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '';
  const seconds = Math.round((new Date(iso).getTime() - now) / 1000);
  const rtf = new Intl.RelativeTimeFormat(currentLocale(), { numeric: 'auto' });
  for (const [unit, size] of UNITS) {
    if (Math.abs(seconds) >= size) return rtf.format(Math.round(seconds / size), unit);
  }
  return rtf.format(0, 'second');
}

export function formatNumber(value: number, options: Intl.NumberFormatOptions = {}): string {
  return new Intl.NumberFormat(currentLocale(), options).format(value);
}

/** Human duration from seconds (e.g. "2 d 4 h"). */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days} d ${hours} h`;
  if (hours > 0) return `${hours} h ${minutes} min`;
  return `${Math.max(1, minutes)} min`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${formatNumber(bytes / 1024, { maximumFractionDigits: 1 })} KB`;
  return `${formatNumber(bytes / (1024 * 1024), { maximumFractionDigits: 1 })} MB`;
}
